import { ReactNode, useEffect, useRef, useState } from "react";
import {
  init,
  getInstance,
  IAssignmentLogger,
  IAssignmentEvent,
} from "@eppo/js-client-sdk";
import { useSession } from "next-auth/react";
import {
  EppoFeatureFlagContext,
  EPPO_FEATURE_FLAG_CONTEXT_DEFAULT_VALUE,
} from "src/contexts/eppoFeatureFlag.context";
import {
  defaultFeatureFlags,
  featureFlagsConfig,
} from "src/featureFlagsConfig/config";
import {
  EppoFeatureFlags,
  FeatureFlagName,
  FeatureFlagOption,
} from "src/types/featureFlags";
import { useSegment } from "src/hooks/useSegment";
import { OTRIUM_NO_GATE_ENABLED } from "src/constants/gatedItem";
import { eppoApiKey } from "src/config/app-config";
import { featureFlagsStore } from "src/stores/feature-flags/feature-flags.store";

function getAnonymousId() {
  let anonId = localStorage.getItem("anonId");
  if (!anonId) {
    anonId = "anon_" + Math.random().toString(36).substr(2, 9);
    localStorage.setItem("anonId", anonId);
  }
  return anonId;
}

export const EppoRandomizationProvider = ({
  children,
  locale,
}: {
  children: ReactNode;
  locale: string;
}): JSX.Element => {
  const { eppoRandomizedAssigment } = useSegment();
  const [featureFlags, setFeatureFlags] = useState<EppoFeatureFlags>(
    EPPO_FEATURE_FLAG_CONTEXT_DEFAULT_VALUE
  );
  const [featureFlagsInitialized, setFeatureFlagsInitialized] =
    useState<boolean>(false);
  const [eppoReady, setEppoReady] = useState<boolean>(false);
  const { data: session } = useSession();

  const eppoInitializedRef = useRef(false);

  useEffect(() => {
    if (!eppoReady || !session || featureFlagsInitialized) {
      return;
    }

    const initializeEppo = () => {
      const subjectKey = session?.user?.user_id || getAnonymousId();
      const isGYesEnabled = localStorage.getItem(OTRIUM_NO_GATE_ENABLED);

      if (!subjectKey) {
        return;
      }

      try {
        let filteredFlags = featureFlagsConfig;
        if (!session.user.user_id) {
          const filteredConfig = Object.entries(featureFlagsConfig).filter(
            ([key]) => !key.toLowerCase().includes("carousel")
          );
          filteredFlags = Object.fromEntries(filteredConfig);
        }
        const eppoClient = getInstance();
        const fetchedFlags = Object.entries(filteredFlags).reduce(
          (acc: EppoFeatureFlags, [flagName, config]) => {
            const {
              flagKey,
              options = {},
              defaultValue,
            } = config as FeatureFlagOption;

            options.locale = locale;
            // Lets a flag's targeting rules tell signed-in shoppers from guests, by the same
            // test that picks the subject key above.
            options.signed_in = Boolean(session.user.user_id);

            if (
              flagName === "pdpCarouselRecommender" ||
              flagName === "pdpTrendingCarouselRecommender" ||
              flagName === "homepageNewInCarouselRecommender" ||
              flagName === "cartYMALCarouselRecommender" ||
              flagName === "recommendationEngineRouting" ||
              flagName === "cartAlertBanner"
            ) {
              options.email = session?.user?.email || "";
            }

            if (
              flagName === "pdpTrendingCarouselRecommender" &&
              typeof acc.pdpCarouselRecommender === "string"
            ) {
              options.dependent_flag =
                "pdp-carousel-you-may-also-like-recommender";
              options.variation_served = acc.pdpCarouselRecommender;
            }

            let flagValue;

            switch (typeof defaultValue) {
              case "string":
                flagValue = eppoClient.getStringAssignment(
                  flagKey,
                  subjectKey,
                  options as Record<string, any>,
                  defaultValue
                );
                break;
              case "object":
                flagValue = eppoClient.getJSONAssignment(
                  flagKey,
                  subjectKey,
                  options as Record<string, any>,
                  defaultValue as object
                );
                break;
              default:
                flagValue = eppoClient.getBooleanAssignment(
                  flagKey,
                  subjectKey,
                  options as Record<string, any>,
                  defaultValue
                );
                break;
            }

            flagValue =
              flagName === "enableGatedHomepage" && isGYesEnabled
                ? false
                : flagValue;

            acc[flagName as FeatureFlagName] = flagValue;

            return acc;
          },
          defaultFeatureFlags
        );
        setFeatureFlagsInitialized(true);
        setFeatureFlags(fetchedFlags);
      } catch (error) {
        console.error(error);
        featureFlagsStore.markResolved();
      }
    };

    initializeEppo();
  }, [featureFlagsInitialized, locale, session, eppoReady]);

  useEffect(() => {
    if (featureFlagsInitialized) {
      featureFlagsStore.markResolved();
    }
  }, [featureFlagsInitialized]);

  useEffect(() => {
    if (eppoInitializedRef.current) {
      return;
    }

    const assignmentLogger: IAssignmentLogger = {
      logAssignment(assignment: IAssignmentEvent) {
        void eppoRandomizedAssigment(assignment);
      },
    };

    init({
      apiKey: eppoApiKey,
      assignmentLogger,
    })
      .then(() => {
        eppoInitializedRef.current = true;
        setEppoReady(true);
      })
      .catch((error) => {
        console.error(error);
        featureFlagsStore.markResolved();
      });
  }, [eppoRandomizedAssigment]);

  return (
    <EppoFeatureFlagContext.Provider value={featureFlags}>
      {children}
    </EppoFeatureFlagContext.Provider>
  );
};
